import { createSelector } from '@reduxjs/toolkit'
import {
  ButtonActionType,
  ButtonBlockAction,
  ButtonNavigationAction,
} from '@vedalib/editor/lib/elements'
import type { ButtonElementValue, BaseButtonActionConfig } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files/types'
import saveAs from 'file-saver'
import lodash from 'lodash'
import * as R from 'ramda'

import { getCorrectLink } from 'components/Editor/EditorElements/elements/Button/helper'
import { EMPTY_OBJECT } from 'constants/commonConstans'
import { BlockModeEnum } from 'gql/__generated__/graphql'
import type { BrandType } from 'services/Branding/types'
import { getFileByUsage } from 'utils/files'

import store from '../store'
import {
  continueAndHideBlock,
  continueBlock,
  courseContinue,
  courseEnd,
  courseStart,
  setCourseNavigation,
  questionReset,
  questionValidate,
  testEnd,
  testRestart,
  testStart,
  answerReset,
} from './actions'
import { SectionTypeEnum } from './enums'
import { useProjectContext, useProjectDispatch } from './hooks'
import { checkPassedTestSection } from './mutators/test'
import type { ICourseNavigationPayload, ProjectActionsTypes } from './reducer'
import {
  getBlockElementsState,
  getBlock,
  getBlocks,
  getSection,
  isDisabledNavigation,
  getEndCourseDisabled,
  getContinueTestQuestionId,
  getSectionState,
  getBlockState,
  getBlockId,
  getSectionId,
  isSectionDisabled,
  getCanRetryTest,
  isFeedbackEnabledInTestElement,
  isFeedbackEnabledInElement,
  getIsSuccessResult,
} from './selectors'
import { getContext } from './selectors/context'
import type { IProjectContext, Section } from './types'

const NOOP_TRIGGER = {
  action: 'noop',
  disabled: true,
  actionCreator: () => EMPTY_OBJECT,
} as const

const HIDE_TRIGGER = {
  action: 'noop',
  hide: true,
  actionCreator: () => EMPTY_OBJECT,
} as const

type ISubmitTriggerAction =
  | ProjectActionsTypes[
      | 'courseStart'
      | 'courseContinue'
      | 'questionValidate'
      | 'setCourseNavigation'
      | 'answerReset'
      | 'questionReset'
      | 'testStart'
      | 'testEnd'
      | 'courseEnd'
      | 'testRestart'
      | 'continueBlock'
      | 'continueAndHideBlock']
  | 'noop'

type ISubmitTriggerActionCreator = () => {
  type: ISubmitTriggerAction
  payload?: ICourseNavigationPayload | string
  disabled?: boolean
}

interface ISubmitTrigger {
  action: ISubmitTriggerAction
  secondAction?: ISubmitTriggerAction
  disabled?: boolean
  actionCreator: ISubmitTriggerActionCreator
  secondActionCreator?: ISubmitTriggerActionCreator
  // TODO: consider rewriting to thunks or extraActionCreators array
  thirdActionCreator?: ISubmitTriggerActionCreator
  secondDisabled?: boolean
  hide?: boolean
  tooltip?: string
  label?: string
  decisionResult?: boolean
}

export const getStartContinueTrigger = createSelector(
  (state: IProjectContext) => state.state.course,
  (courseState): ISubmitTrigger => {
    if (courseState.status === 'not attempted') {
      return {
        action: 'project/courseStart',
        actionCreator: () => courseStart(),
      }
    } else {
      return {
        action: 'project/courseContinue',
        actionCreator: () => courseContinue(),
      }
    }
  },
)

export const getNavigationTrigger = (
  state: IProjectContext,
  params: {
    sectionId: string
    blockId?: string
    currentSection?: Section | null
    type?: ButtonNavigationAction
  },
): ISubmitTrigger => {
  const { sectionId, blockId, currentSection, type } = params
  const section = getSection(state, { id: sectionId })
  const block = getBlock(state, { id: blockId })
  const nextTest = sectionId && section?.type === SectionTypeEnum.test
  const passed = checkPassedTestSection(state, currentSection?.id || '', currentSection?.test)
  const disabled = currentSection?.type === SectionTypeEnum.test ? !passed : false

  if (isDisabledNavigation(state) || isSectionDisabled(state, sectionId)) {
    // disable navigation to the next section if it is a final test
    return NOOP_TRIGGER
  }

  if (nextTest) {
    const testState = getSectionState(state, { id: sectionId })
    let blockId: string | undefined = ''
    const blocks = getBlocks(state, sectionId)
    if (testState?.status === 'notStarted') {
      blockId = blocks.find((block) => block.mode === 'start')?.uuid || R.head(blocks)?.uuid
    } else if (testState?.status === 'finished') {
      blockId = blocks.find((block) => block.mode === 'end')?.uuid || R.last(blocks)?.uuid
    } else {
      blockId = getContinueTestQuestionId(state, sectionId)
    }

    if (!blockId) {
      // throw new Error('blockId is undefined')  //case for no start block
      return NOOP_TRIGGER
    }

    return {
      action: 'project/setCourseNavigation',
      disabled,
      actionCreator: () =>
        setCourseNavigation({
          sectionId,
          blockId,
          fromSectionId: currentSection?.id,
          fromSectionName: currentSection?.name,
          fromSectionType: currentSection?.type,
        }),
    }
  }

  const navigateBlockId = block?.uuid || section?.blocksOrder[0]

  if (!navigateBlockId || !section) {
    return NOOP_TRIGGER
  }

  return {
    action: 'project/setCourseNavigation',
    disabled,
    actionCreator: () =>
      setCourseNavigation({
        sectionId,
        blockId: navigateBlockId,
        ...(type === ButtonNavigationAction.custom && { scrollBlockId: navigateBlockId }),
        fromSectionId: currentSection?.id || null,
        fromSectionName: currentSection?.name,
        fromSectionType: currentSection?.type,
      }),
  }
}

export const useNavigationAction = () => {
  const dispatch = useProjectDispatch()
  const state = useProjectContext(getContext)
  return (params: { sectionId: string; blockId?: string; currentSection?: Section | null }) => {
    const trigger = getNavigationTrigger(state, params)
    if (!trigger.disabled) {
      dispatch(trigger.actionCreator())
    }
  }
}

export const getNextSectionTrigger = createSelector(
  (state: IProjectContext) => state,
  (state: IProjectContext) => getSection(state, { next: true })?.id,
  (state, sectionId) => {
    if (!sectionId) {
      return getEndCourseTrigger(state)
    }

    return getNavigationTrigger(state, { sectionId, currentSection: getSection(state) })
  },
)

export const getPrevSectionTrigger = createSelector(
  (state: IProjectContext) => state,
  (state: IProjectContext) => getSection(state, { prev: true })?.id,
  (state, sectionId): ISubmitTrigger => {
    if (!sectionId) {
      return NOOP_TRIGGER
    }
    return getNavigationTrigger(state, { sectionId: sectionId, currentSection: getSection(state) })
  },
)

export const getNavigationLinkTrigger = (
  state: IProjectContext,
  params: { type: ButtonNavigationAction; sectionId: string; blockId?: string },
) => {
  const { type, sectionId, blockId } = params
  const currentSection = getSection(state)
  if (type === ButtonNavigationAction.nextSection) {
    return getNextSectionTrigger(state)
  } else if (type === ButtonNavigationAction.prevSection) {
    return getPrevSectionTrigger(state)
  } else if (type === ButtonNavigationAction.custom) {
    return getNavigationTrigger(state, { sectionId, blockId, currentSection, type })
  }

  throw new Error(`Unknown navigation action type: ${type}`)
}

export const getTestStartTrigger = createSelector(
  (state: IProjectContext) => state,
  (state): ISubmitTrigger => {
    const block = getBlock(state)
    if (block && block.mode !== 'start') {
      return NOOP_TRIGGER
    }

    return {
      action: 'project/testStart',
      disabled: false,
      actionCreator: () => testStart(),
    }
  },
)

export const getTestNextTrigger = (
  state: IProjectContext,
  params?: { sectionId?: string; currentSectionId?: string | null },
): ISubmitTrigger => {
  const { sectionId = getSectionId(state) } = params || {}
  const blockId = getBlockId(state)
  if (!blockId || !sectionId) {
    console.error('Cannot get next block without sectionId and blockId')
    return NOOP_TRIGGER
  }

  const nextBlock = getBlock(state, { next: true })
  if (nextBlock?.mode === BlockModeEnum.end || !nextBlock) {
    return getTestEndTrigger(state)
  } else {
    return {
      action: 'project/setCourseNavigation',
      disabled: false,
      actionCreator: () =>
        setCourseNavigation({
          sectionId,
          blockId: nextBlock.uuid,
          fromSectionId: params?.currentSectionId,
          fromSectionName: getSection(state)?.name,
          fromSectionType: getSection(state)?.type,
        }),
    }
  }
}

interface IGetTestSubmitTriggerParams {
  sectionId?: string
  blockId?: string
  pt: (key: keyof BrandType['labels']) => string
}

export const getTestSubmitTrigger = createSelector(
  (state: IProjectContext) => state,
  (_state: IProjectContext, params: IGetTestSubmitTriggerParams) => params,
  (state: IProjectContext, params: IGetTestSubmitTriggerParams): ISubmitTrigger => {
    const { sectionId = getSectionId(state), blockId = getBlockId(state), pt } = params
    const blockState = getBlockState(state, blockId)
    const block = getBlock(state, { id: blockId })

    const section = sectionId ? getSection(state, { id: sectionId }) : null
    if (!blockState || !section || !sectionId || !blockId) {
      return NOOP_TRIGGER
    }

    if (!blockState.result) {
      const isEnabledFeedbackInTestEl = isFeedbackEnabledInTestElement(state, blockId, section)
      const {
        action: secondAction,
        actionCreator: secondActionCreator,
        disabled: secondDisabled,
      } = isEnabledFeedbackInTestEl ? {} : getTestNextTrigger(state, { sectionId })
      return {
        action: 'project/questionValidate',
        disabled: !blockState.isReady,
        actionCreator: () => questionValidate({ blockId }),
        secondAction,
        secondActionCreator,
        secondDisabled,
      }
    }

    if (section?.type === SectionTypeEnum.test) {
      return getTestNextTrigger(state, { sectionId })
    } else if (section?.type === SectionTypeEnum.landing) {
      const attemptsLeft = (block?.test?.attempts || 1) - (blockState?.currentTry || 0)
      const tooltip = `${pt('button.attempts')} ${attemptsLeft}`
      const resultValid = blockState.result?.isValid
      if (block?.test?.repeat && block.test?.limit) {
        return {
          action: 'project/questionReset',
          disabled: attemptsLeft ? false : true,
          actionCreator: () => questionReset(blockId),
          tooltip,
        }
      }

      if (!block?.test?.repeat && block?.test?.limit) {
        return {
          action: 'project/questionReset',
          disabled: !resultValid && !!attemptsLeft ? false : true,
          actionCreator: () => questionReset(blockId),
          tooltip: !resultValid ? tooltip : undefined,
          hide: resultValid,
        }
      }

      if (!block?.test?.limit && !block?.test?.repeat) {
        return resultValid
          ? HIDE_TRIGGER
          : {
              action: 'project/questionReset',
              disabled: false,
              actionCreator: () => questionReset(blockId),
            }
      }

      return {
        action: 'project/questionReset',
        disabled: false,
        actionCreator: () => questionReset(blockId),
      }
    }
    throw new Error('Invalid block state')
  },
)

export const getTestEndTrigger = createSelector(
  (state: IProjectContext) => state,
  (state): ISubmitTrigger => {
    const block = getBlock(state)
    const section = getSection(state)

    if (section?.type === SectionTypeEnum.test) {
      if (block?.mode === BlockModeEnum.end) {
        return getNextSectionTrigger(state)
      }
      if (block?.mode === BlockModeEnum.questions) {
        return {
          action: 'project/testEnd' as const,
          disabled: false,
          actionCreator: () => testEnd(),
        }
      }
    }

    return NOOP_TRIGGER
  },
)
interface IGetDecisionTriggerParams {
  blockId?: string
  elementValue: ButtonElementValue
}

export const getDecisionTrigger = createSelector(
  (state: IProjectContext) => state,
  (_state: IProjectContext, params: IGetDecisionTriggerParams) => params,
  (state, params): ISubmitTrigger => {
    const { blockId = getBlockId(state), elementValue } = params
    const { correct, incorrect } = elementValue
    const section = getSection(state)
    const block = getBlock(state, { id: blockId })
    const blockState = getBlockState(state, blockId)
    const blockTestRepeat = block?.test?.repeat
    const blockTestLimit = block?.test?.limit
    const attemptsLeft = (block?.test?.attempts || 1) - (blockState?.currentTry || 0)

    if (section?.type === SectionTypeEnum.test && block?.mode === BlockModeEnum.end) {
      const isSuccess = getIsSuccessResult(state, section.id)
      const variantConfig = isSuccess ? correct : incorrect
      if (!variantConfig) {
        return NOOP_TRIGGER
      }
      return {
        ...buildVariantTrigger({ variantConfig, blockId, state }),
        label: variantConfig.label,
        decisionResult: isSuccess,
      }
    }

    if (!blockState || !section || !blockId) {
      return NOOP_TRIGGER
    }

    const isValid = blockState.result?.isValid
    const variantConfig = isValid ? correct : incorrect
    const canRepeat =
      (blockTestLimit && (attemptsLeft > 0 || block?.test?.attempts === null)) ||
      (blockTestRepeat && !blockTestLimit)

    if (!blockState.result) {
      const isEnabledFeedbackInTestEl = isFeedbackEnabledInElement(state, blockId, section)
      return {
        action: 'project/questionValidate',
        disabled: !blockState.isReady,
        actionCreator: () => questionValidate({ blockId }),
        ...(isEnabledFeedbackInTestEl
          ? {}
          : {
              secondActionCreator: () => {
                const currentState = store.getState().project
                const currentBlockState = getBlockState(currentState, blockId)
                const variantCfg = currentBlockState?.result?.isValid ? correct : incorrect
                if (!variantCfg) {
                  return { type: 'noop' }
                }
                return buildVariantTrigger({
                  variantConfig: variantCfg,
                  blockId,
                  state: currentState,
                }).actionCreator()
              },
              ...(canRepeat && {
                thirdActionCreator: () => {
                  const currentState = store.getState().project
                  const currentBlockState = getBlockState(currentState, blockId)
                  const variantCfg = currentBlockState?.result?.isValid ? correct : incorrect
                  const hasAttemptLeft = block?.test?.attempts === null || attemptsLeft > 1
                  if (variantCfg?.actionType === ButtonActionType.navigation && hasAttemptLeft) {
                    return questionReset(blockId)
                  }
                  return { type: 'noop' as const }
                },
              }),
            }),
      }
    }

    if (!variantConfig) {
      return NOOP_TRIGGER
    }

    const baseTrigger = {
      action: 'project/questionReset' as const,
      disabled: false,
      actionCreator: () => questionReset(blockId),
      label: variantConfig.thirdLabel ?? variantConfig.label,
    }

    if (
      variantConfig.actionType === ButtonActionType.blockAction &&
      variantConfig.action === ButtonBlockAction.validate
    ) {
      if (blockTestLimit) {
        if (blockTestRepeat) {
          return { ...baseTrigger, disabled: !attemptsLeft }
        }
        return {
          ...baseTrigger,
          disabled: !isValid && !!attemptsLeft ? false : true,
          hide: !!isValid,
        }
      }

      if (!blockTestLimit && !blockTestRepeat && isValid) {
        return HIDE_TRIGGER
      }

      return baseTrigger
    }

    // block?.test?.attempts === null unlimited attempts
    return {
      ...buildVariantTrigger({ variantConfig, blockId, state }),
      label: variantConfig.label,
      decisionResult: isValid,
      ...(canRepeat && {
        secondAction: baseTrigger.action,
        secondActionCreator: baseTrigger.actionCreator,
      }),
    }
  },
)

export const getTestRetryTrigger = createSelector(
  (state: IProjectContext) => state,
  (state) => {
    return {
      action: 'project/testStart' as const,
      disabled: !getCanRetryTest(state),
      actionCreator: () => testRestart(),
    }
  },
)

export const getEndCourseTrigger = (state: IProjectContext): ISubmitTrigger => {
  return {
    action: 'project/courseEnd',
    disabled: getEndCourseDisabled(state),
    actionCreator: () => courseEnd(),
  }
}

export const getAnswerResetTrigger = (
  state: IProjectContext,
  params: { blockId?: string },
): ISubmitTrigger => {
  const { blockId = getBlockId(state) } = params
  const blockState = getBlockState(state, blockId)
  const elements = getBlockElementsState(state, blockId)
  const elementsHasValues = Object.values(elements).some((state) => state.value)

  if (!blockId) {
    return NOOP_TRIGGER
  }

  return {
    action: 'project/answerReset',
    disabled: !elementsHasValues || lodash.has(blockState, 'result'),
    actionCreator: () => answerReset(blockId),
  }
}

export const buildVariantTrigger = ({
  variantConfig,
  blockId,
  state,
}: {
  variantConfig: BaseButtonActionConfig
  blockId?: string
  state: IProjectContext
}): ISubmitTrigger => {
  const { actionType, action, actionData } = variantConfig

  if (actionType === ButtonActionType.continue) {
    if (!blockId) {
      return NOOP_TRIGGER
    }
    return {
      action: variantConfig.showBlock ? 'project/continueBlock' : 'project/continueAndHideBlock',
      actionCreator: () =>
        variantConfig.showBlock ? continueBlock({ blockId }) : continueAndHideBlock({ blockId }),
    }
  }

  if (actionType === ButtonActionType.link) {
    const { target, url } = getCorrectLink(actionData)
    return {
      action: 'noop',
      actionCreator: () => {
        window.open(url, target, 'noreferrer')
        return EMPTY_OBJECT
      },
    }
  }

  if (actionType === ButtonActionType.file) {
    return {
      action: 'noop',
      actionCreator: () => {
        if (actionData && 'file' in actionData) {
          const fileData = getFileByUsage(actionData.file ?? null, FileUsageTypeEnum.common)
          if (fileData?.path) {
            saveAs(fileData.path, `${fileData.name}`)
          }
        }
        return EMPTY_OBJECT
      },
    }
  }

  if (actionType === ButtonActionType.navigation) {
    return getNavigationLinkTrigger(state, {
      type: action as ButtonNavigationAction,
      sectionId: (actionData && 'sectionId' in actionData && actionData.sectionId) || '',
      blockId: actionData && 'blockId' in actionData ? actionData.blockId : undefined,
    })
  }

  if (actionType === ButtonActionType.blockAction) {
    switch (action) {
      case ButtonBlockAction.testEnd:
        return getTestEndTrigger(state)
      case ButtonBlockAction.testRestart:
        return getTestRetryTrigger(state)
      case ButtonBlockAction.courseEnd:
        return getEndCourseTrigger(state)
      case ButtonBlockAction.testStart:
        return getTestStartTrigger(state)
      case ButtonBlockAction.courseStart:
        return getStartContinueTrigger(state)
      case ButtonBlockAction.reset:
        return getAnswerResetTrigger(state, { blockId })
      default:
        return NOOP_TRIGGER
    }
  }

  return NOOP_TRIGGER
}
